import { useState, useEffect } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCookieSync } from '../contexts/CookieSyncContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, BookOpen, Play, Brain, ShoppingBag, TrendingUp, ChevronRight, RefreshCw, Target, Bot, History, Heart, Bookmark, ArrowRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get SEO-friendly content URL
const getContentUrl = (item) => `/content/${item.slug || item.content_id}`;

export default function Dashboard() {
  const { user, updateUser } = useAuth();
  const { getTopInterests, fetchPersonalizedFeed, personalizedFeed: cookieFeed, loading: cookieLoading } = useCookieSync();
  const [dailyAdvice, setDailyAdvice] = useState(null);
  const [personalizedFeed, setPersonalizedFeed] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [likedContent, setLikedContent] = useState([]);
  const [bookmarkedContent, setBookmarkedContent] = useState([]);
  const [recommendedDeals, setRecommendedDeals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedPage, setFeedPage] = useState(1);
  const [hasMoreFeed, setHasMoreFeed] = useState(true);
  const [topInterests, setTopInterests] = useState([]);

  // Set page title
  useEffect(() => {
    document.title = 'Dashboard | SkinUse';
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('skinuse_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    const fetchData = async () => {
      try {
        // Fetch all data in parallel
        const [adviceRes, feedRes, dealsRes, analyticsRes, recentRes, likedRes, bookmarksRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/advice/daily`, { headers }),
          fetch(`${BACKEND_URL}/api/content/personalized/feed?limit=6`, { headers }),
          fetch(`${BACKEND_URL}/api/deals/recommended`, { headers }),
          fetch(`${BACKEND_URL}/api/analytics/user`, { headers }),
          fetch(`${BACKEND_URL}/api/content/recently-viewed?limit=5`, { headers }),
          fetch(`${BACKEND_URL}/api/content/user/liked?limit=5`, { headers }),
          fetch(`${BACKEND_URL}/api/content/user/bookmarks?limit=5`, { headers })
        ]);

        if (adviceRes.ok) setDailyAdvice(await adviceRes.json());
        if (feedRes.ok) {
          const feedData = await feedRes.json();
          setPersonalizedFeed(feedData);
          setHasMoreFeed(feedData.length >= 6);
        }
        if (dealsRes.ok) setRecommendedDeals(await dealsRes.json());
        if (analyticsRes.ok) {
          const data = await analyticsRes.json();
          setAnalytics(data);
          updateUser({ skin_points: data.skin_points });
        }
        if (recentRes.ok) setRecentlyViewed(await recentRes.json());
        if (likedRes.ok) setLikedContent(await likedRes.json());
        if (bookmarksRes.ok) setBookmarkedContent(await bookmarksRes.json());
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Fetch Cookie-Sync personalized feed
    fetchPersonalizedFeed();
    setTopInterests(getTopInterests());
  }, [updateUser, fetchPersonalizedFeed, getTopInterests]);

  // Load more feed content
  const loadMoreFeed = async () => {
    if (loadingMore || !hasMoreFeed) return;
    setLoadingMore(true);
    
    try {
      const token = localStorage.getItem('skinuse_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const skip = personalizedFeed.length;
      
      const res = await fetch(`${BACKEND_URL}/api/content/personalized/feed?limit=4&skip=${skip}`, { headers });
      if (res.ok) {
        const newItems = await res.json();
        if (newItems.length > 0) {
          setPersonalizedFeed(prev => [...prev, ...newItems]);
          setHasMoreFeed(newItems.length >= 4);
        } else {
          setHasMoreFeed(false);
        }
      }
    } catch (error) {
      console.error('Load more error:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Use Cookie-Sync feed if it has more content, otherwise use API feed
  useEffect(() => {
    if (cookieFeed && cookieFeed.length > 0) {
      // Merge and deduplicate feeds, prioritizing cookie-sync recommendations
      const mergedFeed = [...cookieFeed];
      personalizedFeed.forEach(item => {
        if (!mergedFeed.find(c => c.content_id === item.content_id)) {
          mergedFeed.push(item);
        }
      });
      setPersonalizedFeed(mergedFeed.slice(0, 8));
    }
  }, [cookieFeed]);

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return Play;
      case 'article': return BookOpen;
      case 'knowledge': return Brain;
      default: return TrendingUp;
    }
  };

  const getContentLabel = (type) => {
    switch (type) {
      case 'news': return 'Daily Drop';
      case 'article': return 'Deep Dive';
      case 'video': return 'Video';
      case 'knowledge': return 'Knowledge';
      default: return 'Content';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="dashboard">
      <SEO {...PAGE_SEO.dashboard} url="/dashboard" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold mb-2">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground">Here's your personalized skin & hair care intelligence</p>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gold/10 border-none" data-testid="stat-skinpoints">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-gold/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-2xl font-serif font-semibold text-gold">{analytics?.skin_points || user?.skin_points || 0}</p>
                    <p className="text-xs text-muted-foreground">SkinPoints</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-none shadow-card" data-testid="stat-articles">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-2xl font-serif font-semibold">{analytics?.articles_read || 0}</p>
                    <p className="text-xs text-muted-foreground">Articles Read</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-none shadow-card" data-testid="stat-quizzes">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <Brain className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-2xl font-serif font-semibold">{analytics?.quizzes_taken || 0}</p>
                    <p className="text-xs text-muted-foreground">Quizzes Taken</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-none shadow-card" data-testid="stat-accuracy">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-2xl font-serif font-semibold">{analytics?.quiz_accuracy || 0}%</p>
                    <p className="text-xs text-muted-foreground">Quiz Accuracy</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quiz Banner */}
          <Card className="mb-8 overflow-hidden bg-gradient-to-r from-gold/20 via-gold/10 to-transparent border-gold/30" data-testid="quiz-banner">
            <CardContent className="p-0">
              <div className="flex flex-col sm:flex-row items-center justify-between p-6">
                <div className="flex items-center gap-4 mb-4 sm:mb-0">
                  <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center">
                    <Brain className="w-7 h-7 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold">Test Your Beauty Knowledge</h3>
                    <p className="text-sm text-muted-foreground">Take a quiz and earn up to <span className="text-gold font-semibold">+100 SkinPoints</span></p>
                  </div>
                </div>
                <Link to="/quiz">
                  <Button className="btn-primary gap-2 group" data-testid="take-quiz-banner-btn">
                    Take Quiz Now
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-8">
              {/* Daily Advice Widget */}
              {dailyAdvice && (
                <Card className="bg-card border-none shadow-float overflow-hidden" data-testid="daily-advice">
                  <div className="h-1 gold-shimmer"></div>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-gold" />
                        <span className="text-xs font-bold uppercase tracking-wider text-gold">Daily Advice</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Refreshes in 24h</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h3 className="font-serif text-xl font-semibold mb-2">{dailyAdvice.title}</h3>
                    <div 
                      className="text-muted-foreground leading-relaxed prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: dailyAdvice.content }}
                    />
                    <div className="flex flex-wrap gap-2 mt-4">
                      {dailyAdvice.tags?.map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-secondary text-xs rounded-sm">{tag}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Personalized Feed */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-2xl font-semibold">For You</h2>
                  <Link 
                    to="/daily-drop" 
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 hover:bg-gold/20 text-gold font-semibold text-sm rounded-full transition-all group" 
                    data-testid="view-all-feed"
                  >
                    View All 
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {personalizedFeed.map((item) => {
                    const Icon = getContentIcon(item.content_type);
                    return (
                      <Link key={item.content_id} to={getContentUrl(item)} data-testid={`feed-item-${item.content_id}`}>
                        <Card className="bg-card border-none shadow-card card-hover h-full group">
                          <div className="aspect-video overflow-hidden relative">
                            <img
                              src={item.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'}
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            {item.likes > 0 && (
                              <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs">
                                <Heart className="w-3 h-3 fill-current" />
                                {item.likes}
                              </div>
                            )}
                          </div>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Icon className="w-3 h-3 text-gold" />
                              <span className="text-xs font-semibold uppercase tracking-wider text-gold">
                                {getContentLabel(item.content_type)}
                              </span>
                            </div>
                            <h3 className="font-serif font-semibold mb-1 line-clamp-2">{item.title}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2">{item.summary}</p>
                            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                              <span>{item.read_time} min read</span>
                              <span className="flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-gold" />
                                +{item.points_reward}
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
                
                {/* Load More Button */}
                {hasMoreFeed && (
                  <div className="flex justify-center mt-8">
                    <Button 
                      onClick={loadMoreFeed} 
                      disabled={loadingMore}
                      variant="outline"
                      className="px-8 py-3 h-12 font-semibold border-2 hover:bg-secondary/50"
                      data-testid="load-more-feed"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load More Content'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card className="bg-card border-none shadow-card" data-testid="quick-actions">
                <CardHeader className="pb-3">
                  <h3 className="font-serif text-lg font-semibold">Quick Actions</h3>
                </CardHeader>
                <CardContent className="space-y-2 pt-0">
                  <Link to="/ai-advisor">
                    <Button variant="outline" className="w-full justify-start gap-3 border-gold/30 hover:bg-gold/10 h-11" data-testid="action-ai-advisor">
                      <Bot className="w-4 h-4 text-gold" />
                      AI Beauty Advisor
                      <span className="ml-auto text-xs text-gold">New</span>
                    </Button>
                  </Link>
                  <Link to="/quiz">
                    <Button variant="outline" className="w-full justify-start gap-3 h-11" data-testid="action-quiz">
                      <Brain className="w-4 h-4 text-gold" />
                      Take a Quiz
                      <span className="ml-auto text-xs text-gold">+20 pts</span>
                    </Button>
                  </Link>
                  <Link to="/deals">
                    <Button variant="outline" className="w-full justify-start gap-3 h-11" data-testid="action-deals">
                      <ShoppingBag className="w-4 h-4" />
                      Browse Deals
                    </Button>
                  </Link>
                  <Link to="/profile">
                    <Button variant="outline" className="w-full justify-start gap-3 h-11" data-testid="action-profile">
                      <RefreshCw className="w-4 h-4" />
                      Update Profile
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Recently Viewed */}
              {recentlyViewed.length > 0 && (
                <Card className="bg-card border-none shadow-card" data-testid="recently-viewed">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <History className="w-4 h-4 text-gold" />
                      <h3 className="font-serif text-lg font-semibold">Recently Viewed</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">Continue where you left off</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {recentlyViewed.map((item) => {
                      const Icon = getContentIcon(item.content_type);
                      return (
                        <Link 
                          key={item.content_id} 
                          to={getContentUrl(item)} 
                          className="flex gap-3 group"
                          data-testid={`recent-item-${item.content_id}`}
                        >
                          <div className="w-16 h-12 rounded-sm overflow-hidden flex-shrink-0">
                            <img 
                              src={item.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'} 
                              alt={item.title} 
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium line-clamp-2 group-hover:text-gold transition-colors">{item.title}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <Icon className="w-3 h-3" />
                              <span>{getContentLabel(item.content_type)}</span>
                              <span>·</span>
                              <span>{item.read_time} min</span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Liked Content */}
              {likedContent.length > 0 && (
                <Card className="bg-card border-none shadow-card" data-testid="liked-content">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-red-500 fill-current" />
                      <h3 className="font-serif text-lg font-semibold">Liked Content</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">Your favorites</p>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                    {likedContent.map((item) => {
                      const Icon = getContentIcon(item.content_type);
                      return (
                        <Link 
                          key={item.content_id} 
                          to={getContentUrl(item)} 
                          className="flex gap-3 group"
                          data-testid={`liked-item-${item.content_id}`}
                        >
                          <div className="w-16 h-12 rounded-sm overflow-hidden flex-shrink-0">
                            <img 
                              src={item.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'} 
                              alt={item.title} 
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium line-clamp-2 group-hover:text-gold transition-colors">{item.title}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <Icon className="w-3 h-3" />
                              <span>{getContentLabel(item.content_type)}</span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Bookmarked Content */}
              {bookmarkedContent.length > 0 && (
                <Card className="bg-card border-none shadow-card" data-testid="bookmarked-content">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Bookmark className="w-4 h-4 text-gold fill-current" />
                      <h3 className="font-serif text-lg font-semibold">Saved for Later</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">Your reading list</p>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                    {bookmarkedContent.map((item) => {
                      const Icon = getContentIcon(item.content_type);
                      return (
                        <Link 
                          key={item.content_id} 
                          to={getContentUrl(item)} 
                          className="flex gap-3 group"
                          data-testid={`bookmarked-item-${item.content_id}`}
                        >
                          <div className="w-16 h-12 rounded-sm overflow-hidden flex-shrink-0">
                            <img 
                              src={item.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'} 
                              alt={item.title} 
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium line-clamp-2 group-hover:text-gold transition-colors">{item.title}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <Icon className="w-3 h-3" />
                              <span>{getContentLabel(item.content_type)}</span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Your Interests - Cookie-Sync Based */}
              {topInterests.length > 0 && (
                <Card className="bg-gold/5 border-none shadow-card" data-testid="browsing-interests">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-gold" />
                      <h3 className="font-serif text-lg font-semibold">Based on Your Browsing</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">Content personalized from your activity</p>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-2">
                      {topInterests.map((interest, i) => (
                        <div key={i} className="flex items-center gap-1 px-3 py-1.5 bg-gold/10 text-xs font-medium rounded-sm">
                          <span className="text-gold">{interest.label}</span>
                          <span className="text-muted-foreground">({Math.round(interest.score)})</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Your Profile Interests */}
              {analytics?.interests?.length > 0 && (
                <Card className="bg-card border-none shadow-card" data-testid="user-interests">
                  <CardHeader className="pb-3">
                    <h3 className="font-serif text-lg font-semibold">Your Interests</h3>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-2">
                      {analytics.interests.slice(0, 8).map((interest, i) => (
                        <span key={i} className="px-3 py-1.5 bg-secondary text-xs font-medium rounded-sm">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recommended Deals */}
              {recommendedDeals.length > 0 && (
                <Card className="bg-card border-none shadow-card" data-testid="recommended-deals">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-lg font-semibold">Deals for You</h3>
                      <Link to="/deals" className="text-xs text-gold hover:underline">View All</Link>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {recommendedDeals.slice(0, 3).map((deal) => (
                      <div key={deal.product_id} className="flex gap-3">
                        <div className="w-16 h-16 rounded-sm overflow-hidden flex-shrink-0">
                          <img src={deal.image_url} alt={deal.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{deal.name}</p>
                          <p className="text-xs text-muted-foreground">{deal.brand}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm font-semibold text-gold">${deal.discounted_price}</span>
                            <span className="text-xs text-muted-foreground line-through">${deal.original_price}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
